/**
 * apiClient — the ONE configured axios instance every API call goes through.
 *
 * WHY A SHARED INSTANCE INSTEAD OF CALLING axios.get() EVERYWHERE
 * --------------------------------------------------------------
 * Sprinkling `axios.get('http://localhost:5000/...')` across forty components
 * means the base URL is hardcoded forty times, and the day we add auth tokens
 * we would edit forty files. With one configured instance:
 *   • the base URL is set once (from the environment)
 *   • the auth token will attach in ONE place (the request interceptor below)
 *   • every response is unwrapped and every error normalised in ONE place
 *
 * THE TWO INTERCEPTORS ARE THE WHOLE VALUE OF THIS FILE:
 *   request  — runs before every call leaves the browser (attach token later)
 *   response — runs on every reply (unwrap our envelope / normalise errors)
 */

import axios from 'axios';
import { syncFromDateHeader } from './serverClock';

/** localStorage key holding the student's session token. */
export const CUSTOMER_TOKEN_KEY = 'rocket07_customer_token';

/* ------------------------------------------------------------------------ *
 * WHICH ADDRESS WE CALL — and why production ignores the absolute URL
 *
 * The API host (`*.up.railway.app`) is IPv4-only: it publishes an A record and
 * no AAAA. Most Indian mobile networks are IPv6-only and reach IPv4 hosts via
 * carrier NAT64, which fails often enough that a large share of phones could
 * load the storefront (Cloudflare is dual-stack) and complete Google sign-in
 * (Google is dual-stack) but never reach our own API — "Cannot reach the
 * server", or a timeout. Some Indian ISPs block `up.railway.app` outright.
 *
 * So in production we call `/api/v1` on our OWN origin, and the Cloudflare
 * Pages Function in functions/api/[[path]].js forwards it to Railway. The
 * phone only ever talks to Cloudflare, which is dual-stack and has points of
 * presence in India. It also removes the CORS preflight from every call.
 *
 * VITE_API_BASE_URL is still honoured, but in a production build an absolute
 * value becomes the FALLBACK rather than the primary address. That is
 * deliberate: it means the fix ships with a plain `git push` and cannot be
 * undone by a stale value left in the Cloudflare dashboard, while a
 * misconfigured or undeployed proxy degrades to exactly today's behaviour
 * instead of taking the storefront down.
 * ------------------------------------------------------------------------ */
const CONFIGURED_BASE = import.meta.env.VITE_API_BASE_URL?.trim() || '';
const IS_ABSOLUTE = /^https?:\/\//i.test(CONFIGURED_BASE);

/** Same-origin in production; whatever .env says during local development. */
const PRIMARY_BASE = import.meta.env.PROD
  ? '/api/v1'
  : CONFIGURED_BASE || 'http://localhost:5000/api/v1';

/** Used only if the same-origin proxy turns out not to be answering. */
const FALLBACK_BASE = import.meta.env.PROD && IS_ABSOLUTE ? CONFIGURED_BASE : null;

/**
 * The address currently believed to work. Starts as the proxy and switches to
 * the fallback for the rest of the session the first time the proxy fails, so
 * only one request pays the cost of discovering it.
 */
let activeBase = PRIMARY_BASE;

export const apiClient = axios.create({
  baseURL: PRIMARY_BASE,

  // Abort a request that hangs longer than this so the UI never spins forever.
  // 20s rather than 15s: a Railway container that has gone cold has to boot and
  // reconnect to Atlas, and a phone on weak mobile data adds seconds on top.
  timeout: 20_000,

  headers: { 'Content-Type': 'application/json' },

  // Sends cookies with cross-origin requests. Needed once auth uses cookies;
  // harmless now. Must be paired with the server's CORS `credentials: true`.
  withCredentials: true,
});

/**
 * Whether re-sending this request is safe.
 *
 * Reads are always safe. `/auth/google` is safe too — it exchanges a Google ID
 * token for a session, and doing that twice just returns the session twice.
 *
 * Everything else is not, and the reason is concrete: a POST to `/orders` may
 * have reached the server and created the order with only the REPLY lost. A
 * retry would place a second real order. Those requests fail honestly instead.
 */
function isRetrySafe(config) {
  const method = (config?.method ?? 'get').toLowerCase();
  if (method === 'get' || method === 'head') return true;
  return Boolean(config?.url?.includes('/auth/google'));
}

/**
 * True when a reply is the SPA's index.html rather than API JSON.
 *
 * This is the one failure that a fallback genuinely has to catch. If the Pages
 * Function is missing, `/api/v1/...` matches the `/* -> /index.html 200` rule
 * in public/_redirects, so the request "succeeds" with a page of HTML. Without
 * this check that arrives as a 200 full of nonsense; with it, we recognise the
 * proxy is absent and move to the direct URL.
 */
function isSpaFallbackHtml(response) {
  const type = response?.headers?.['content-type'] ?? '';
  return type.includes('text/html');
}

/* ------------------------------------------------------------------------ *
 * REQUEST INTERCEPTOR
 *
 * Runs just before each request is sent. Today it is a placeholder; when
 * authentication arrives this is the single spot that attaches the token to
 * every outgoing request — no component needs to know about auth.
 * ------------------------------------------------------------------------ */
apiClient.interceptors.request.use(
  (config) => {
    // Attach the student's session token when signed in. Public endpoints
    // (products, colleges) simply ignore it; protected ones require it.
    const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Once a session has learned that the same-origin proxy is not answering,
    // every later request goes straight to the address that works.
    config.baseURL = activeBase;
    return config;
  },
  (error) => Promise.reject(error),
);

/* ------------------------------------------------------------------------ *
 * RESPONSE INTERCEPTOR
 *
 * SUCCESS: our API always replies with { success, message, data }. We return
 * `response.data` (that envelope) so callers never write `.data.data`.
 *
 * FAILURE: we translate axios's messy error into ONE predictable shape so
 * every component and every React Query hook can handle errors identically.
 * ------------------------------------------------------------------------ */
apiClient.interceptors.response.use(
  (response) => {
    // A page of HTML means the proxy is not deployed and the SPA fallback
    // answered instead. Treat it as a failure so the retry path can react.
    if (isSpaFallbackHtml(response)) {
      return retry(response.config, { proxyIsMissing: true });
    }
    // Free clock correction — checkout uses it to name the right delivery day
    // even on a phone whose own clock is wrong. See lib/serverClock.js.
    syncFromDateHeader(response.headers?.date);
    return response.data;
  },
  (error) => {
    // A 401 on a protected call means the session died — clear it and tell the
    // app (CustomerAuthContext listens) to sign out. We ignore 401s on the
    // login call itself, which handles its own error.
    const status = error.response?.status;
    const isLogin = error.config?.url?.includes('/auth/google');
    if (status === 401 && !isLogin) {
      localStorage.removeItem(CUSTOMER_TOKEN_KEY);
      window.dispatchEvent(new Event('rocket07:customer-unauthorized'));
    }

    /**
     * No reply at all — the flaky-mobile case this whole file exists for.
     * One more attempt costs the user a second and rescues a request that hit
     * a moment of bad signal. `error.response` being absent is the tell: the
     * server never spoke, so nothing has happened on its side yet.
     *
     * A 504 from our own proxy counts too — that is the Function telling us it
     * could not reach Railway, which is the same situation one level up.
     */
    const noReply = !error.response;
    const proxyCouldNotReachUpstream =
      status === 504 && error.response?.data?.code?.startsWith('UPSTREAM_');

    if (noReply || proxyCouldNotReachUpstream) {
      return retry(error.config, { proxyIsMissing: false, cause: error });
    }

    return Promise.reject(normalizeError(error));
  },
);

/**
 * Re-sends a failed request once.
 *
 * Two different repairs, in one place because they share the "try again" step:
 *   • switch to the direct API URL when the same-origin proxy is not answering
 *   • simply try again when a mobile connection dropped the request
 *
 * Only ever fires for requests that are safe to repeat, and only once per
 * request — `__rocket07Retried` is what stops this from recursing through the
 * interceptor forever.
 */
function retry(config, { proxyIsMissing, cause }) {
  const giveUp = () =>
    Promise.reject(
      normalizeError(
        cause ?? {
          request: {},
          config,
          message: 'The server sent an unexpected reply.',
        },
      ),
    );

  if (!config || config.__rocket07Retried || !isRetrySafe(config)) return giveUp();

  if (proxyIsMissing) {
    /**
     * The proxy is genuinely not there — it served the SPA's HTML instead of
     * API JSON. That is a deployment fault, not a network blip, so this
     * session moves to the direct URL permanently. With no fallback configured
     * there is nothing to move to.
     */
    if (!FALLBACK_BASE || activeBase === FALLBACK_BASE) return giveUp();
    activeBase = FALLBACK_BASE;
  }

  /**
   * Otherwise the connection simply dropped, and we retry on the SAME address.
   *
   * Deliberately NOT switching to the direct URL here: on the phones this fix
   * targets, that address is the one that cannot be reached. Treating one
   * moment of bad signal as proof the proxy is broken would move the session
   * onto the broken path and undo the entire fix.
   */
  config.__rocket07Retried = true;
  config.baseURL = activeBase;
  return apiClient.request(config);
}

/**
 * Turns any axios failure into a consistent object:
 *   { message, code, status, errors, isNetworkError }
 *
 * There are three distinct failure worlds, and the UI needs to tell them apart:
 *   1. The server replied with an error (4xx/5xx) — we have its JSON body.
 *   2. The request left but got NO reply — server down, or no internet.
 *   3. Something broke before the request was even sent.
 */
function normalizeError(error) {
  // World 1: the server responded with an error status.
  if (error.response) {
    const { status, data } = error.response;
    return {
      message: data?.message ?? 'Something went wrong. Please try again.',
      code: data?.code ?? 'API_ERROR',
      status,
      errors: data?.errors ?? [],
      isNetworkError: false,
    };
  }

  // World 2: the request was made but no response came back.
  if (error.request) {
    const timedOut = error.code === 'ECONNABORTED';
    return {
      message: timedOut
        ? 'The server took too long to respond. Please try again.'
        : 'Cannot reach the server. Check your connection and try again.',
      code: timedOut ? 'TIMEOUT' : 'NETWORK_ERROR',
      status: null,
      errors: [],
      isNetworkError: true,
    };
  }

  // World 3: an error setting up the request.
  return {
    message: error.message ?? 'An unexpected error occurred.',
    code: 'CLIENT_ERROR',
    status: null,
    errors: [],
    isNetworkError: false,
  };
}

export default apiClient;
